import React, { useState, useEffect, useCallback } from 'react';
import { Drawer, List, Avatar, Space, Typography, Empty, Checkbox, Tag, Select, message } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import api from '../api';
import { formatTS } from '../utils/time';
import LinkifiedText from './LinkifiedText';
import CommentBox, { CommentAttachments } from './CommentBox';
import UserAvatar from './UserAvatar';

// Comment log for a client or contact (Admin > Clients), newest first.
// "Show client comments" on a contact / "Show contact comments" on a client
// (on by default) mixes in the other side's comments, each marked with the
// client or contact it was made on.
// Each comment is on one campaign, or General (no campaign); the Campaign
// dropdown lists the record's campaigns, then General, defaulting to the first.
// record: null | { kind: 'clients' | 'client-contacts', id, name, campaigns: [{value, label}] }
// attachmentType: 'client' | 'client_contact'
// RecordComments is the log itself (the client page shows it inline); the
// default export puts it in a side panel (the Clients list).
const GENERAL = 'general';

export function RecordComments({ record, canAdd, onChanged }) {
  const [comments, setComments] = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [adding,   setAdding]   = useState(false);
  const [showRelated, setShowRelated] = useState(true);
  const [campaignId,  setCampaignId]  = useState(GENERAL);

  const url = record ? `/api/${record.kind}/${record.id}/comments` : null;
  const isContact = record?.kind === 'client-contacts';
  const attachmentType = record?.kind === 'clients' ? 'client' : 'client_contact';

  const load = useCallback(async () => {
    if (!url) return;
    setLoading(true);
    try {
      const res = await api.get(url, { params: showRelated ? { show_related: 1 } : {} });
      setComments(res.data || []);
    } catch {
      message.error('Failed to load comments.');
    } finally {
      setLoading(false);
    }
  }, [url, showRelated]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    setComments([]);
    setShowRelated(true);
    setCampaignId(record?.campaigns?.[0]?.value ?? GENERAL);
  }, [url]); // eslint-disable-line react-hooks/exhaustive-deps

  const campaignOptions = [...(record?.campaigns || []), { value: GENERAL, label: 'General' }];

  const add = async (text, files) => {
    setAdding(true);
    try {
      const res = await api.post(url, { comment: text, campaign_id: campaignId === GENERAL ? null : campaignId });
      for (const f of files) {
        const fd = new FormData();
        fd.append('comment_type', attachmentType);
        fd.append('comment_id', String(res.data.id));
        fd.append('file', f.file, f.name);
        await api.post('/api/attachments', fd);
      }
      load();
      onChanged?.();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to add comment.');
    } finally {
      setAdding(false);
    }
  };

  return (
    <>
      {canAdd && (
        <div style={{ marginBottom: 16 }}>
          <Space style={{ marginBottom: 6 }}>
            <Typography.Text>Campaign</Typography.Text>
            <Select size="small" style={{ minWidth: 200 }} value={campaignId} onChange={setCampaignId}
              options={campaignOptions} />
          </Space>
          <CommentBox onSubmit={add} loading={adding} />
        </div>
      )}
      {record && (
        <Checkbox checked={showRelated} onChange={(e) => setShowRelated(e.target.checked)} style={{ marginBottom: 8 }}>
          {isContact ? 'Show client comments' : 'Show contact comments'}
        </Checkbox>
      )}
      {!loading && comments.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /> : (
        <List
          loading={loading}
          dataSource={comments}
          rowKey={(c) => `${c.Source}-${c.ID}`}
          renderItem={(c) => (
            <List.Item style={{ alignItems: 'flex-start', padding: '8px 0' }}>
              <List.Item.Meta
                avatar={<UserAvatar userId={c.UserID} name={c.UserName} size={24} />}
                title={
                  <Space size={8}>
                    <Typography.Text strong style={{ fontSize: 13 }}>{c.UserName}</Typography.Text>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>{formatTS(c.CreatedAt)}</Typography.Text>
                    <Tag>{c.CampaignName || 'General'}</Tag>
                    {c.Source !== record?.kind && <Tag color="blue">{c.SourceName}</Tag>}
                  </Space>
                }
                description={
                  <>
                    <Typography.Text style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}><LinkifiedText text={c.Comment} /></Typography.Text>
                    <CommentAttachments attachments={c.Attachments} />
                  </>
                }
              />
            </List.Item>
          )}
        />
      )}
    </>
  );
}

export default function RecordCommentsDrawer({ record, canAdd, onChanged }) {
  return (
    <Drawer open={!!record} onClose={() => onChanged?.(true)} width={520}
      title={record ? `Comments: ${record.name}` : ''} destroyOnClose>
      {record && <RecordComments record={record} canAdd={canAdd} onChanged={onChanged} />}
    </Drawer>
  );
}
